/**
 * Catalogo autoritativo de definiciones de carta.
 *
 * Es el indice que permite resolver un `DefinitionId` y, con el, comprobar las
 * cosas que hasta ahora no podian comprobarse: cuantas Ofrendas lleva un mazo, o
 * si el `kind` de un ocupante del Campo case con el `type` de su definicion.
 *
 * El catalogo **no** interpreta efectos. `effectText` sigue siendo opaco: aqui
 * no hay DSL, AST, motor de efectos, disparos, objetivos, pila ni scripting, y
 * no deben anadirse.
 */
import { isOfferDefinition } from './cards.js';
/**
 * Construye un catalogo a partir de definiciones sueltas.
 *
 * @throws {Error} Si dos definiciones comparten `definitionId`. Un catalogo con
 * identidades repetidas no puede resolver nada de forma fiable.
 */
export function createCardCatalog(definitions) {
    const byId = new Map();
    for (const definition of definitions) {
        if (byId.has(definition.definitionId)) {
            throw new Error(`El catalogo repite la definicion "${definition.definitionId}".`);
        }
        byId.set(definition.definitionId, definition);
    }
    return { definitions: byId };
}
/** Devuelve la definicion, o `undefined` si el catalogo no la conoce. */
export function findDefinition(catalog, id) {
    return catalog.definitions.get(id);
}
/**
 * Resuelve una definicion que se espera presente.
 *
 * @throws {Error} Si el catalogo no contiene `id`.
 */
export function requireDefinition(catalog, id) {
    const definition = findDefinition(catalog, id);
    if (definition === undefined) {
        throw new Error(`El catalogo no contiene la definicion "${id}".`);
    }
    return definition;
}
/** Cierto si toda entrada de la lista se resuelve contra el catalogo. */
export function isDeckListResolvable(catalog, list) {
    return list.entries.every((entry) => catalog.definitions.has(entry.definitionId));
}
/**
 * Cuenta las Ofrendas declaradas por una lista.
 *
 * Necesita catalogo: la lista solo guarda identidades, y saber si una es Ofrenda
 * exige resolverla.
 *
 * @throws {Error} Si alguna entrada no se resuelve.
 */
export function deckListOfferCount(catalog, list) {
    return list.entries.reduce((total, entry) => {
        const definition = requireDefinition(catalog, entry.definitionId);
        return isOfferDefinition(definition) ? total + entry.quantity : total;
    }, 0);
}
/**
 * Comprueba la coherencia entre un ocupante del Campo y su definicion.
 *
 * Cierra el invariante que `board.ts` solo podia documentar: un ocupante
 * `kind: 'invocation'` cuya definicion sea un Hechizo es un estado corrupto.
 *
 * @throws {Error} Si el catalogo no contiene la definicion del ocupante.
 */
export function matchesDefinitionType(catalog, occupant) {
    const definition = requireDefinition(catalog, occupant.card.definitionId);
    return occupant.kind === 'invocation'
        ? definition.type === 'invocation'
        : definition.type === 'spell';
}
//# sourceMappingURL=catalog.js.map